import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiButton from "@mui/material/Button";
import MuiIconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import MuiSlider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import { BodyTextM } from "../Typography";
import { HeadingL, HeadingM, HeadingS } from "../Typography";

export const PageRoot = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        width: "100%",
        gap: 5,
    }),
);

export const IntroSection = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        px: { xs: 4, md: 6 },
        gap: 3,
    }),
);

export const SliderSection = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        px: { xs: 4, md: 6 },
    }),
);

export const SliderInner = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        px: 4,
    }),
);

export const ResultSection = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        mt: 4,
        py: 5,
        px: { xs: 4, md: 6 },
        borderTop: "1px solid",
        borderBottom: "1px solid",
        borderColor: "grey.300",
        backgroundColor: "#F7F7F7",
        gap: 4,
    }),
);

export const ResultColumns = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 4, md: 0 },
    }),
);

export const ResultColumn = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        width: { md: "50%" },
        mr: { md: "3%" },
        gap: 2,
    }),
);

export const PortalSectionTitle = styled(HeadingM)(({ theme }) =>
    theme.unstable_sx({
        mb: 4,
        color: "primary.main",
    }),
);

export const PortalSectionContent = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        gap: 5,
    }),
);

export const PortalMetrics = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        gap: 3,
    }),
);

export const PortalStrongLabel = styled(BodyTextM)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
        fontWeight: 600,
    }),
);

export const PortalDescriptionLabel = styled(BodyTextM)(({ theme }) =>
    theme.unstable_sx({
        maxWidth: 250,
    }),
);

export const PortalValue = styled(HeadingM)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
    }),
);

export const ResultRow = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    }),
);

export const ContributionHeading = styled(HeadingM)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
        mr: { xs: 0, md: 5 },
        color: "primary.main",
    }),
);

export const MutedColumnTitle = styled(HeadingS)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
        color: "grey.600",
    }),
);

export const ColumnTitle = styled(HeadingS)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
    }),
);

export const MutedLabel = styled(BodyTextM)(({ theme }) =>
    theme.unstable_sx({
        color: "grey.600",
    }),
);

export const MutedValue = styled(HeadingM)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
        color: "grey.600",
    }),
);

export const ComparisonValue = styled(HeadingM)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
    }),
);

export const CtaSection = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        py: 5,
        px: { xs: 4, md: 6 },
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 4, md: 0 },
        alignItems: { md: "center" },
    }),
);

export const CtaTextStack = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        gap: 2,
        flex: 1,
    }),
);

export const CtaHeading = styled(HeadingL)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
        color: "primary.main",
    }),
);

export const CtaDescription = styled(BodyTextM)(({ theme }) =>
    theme.unstable_sx({
        color: "grey.600",
    }),
);

export const CtaButton = styled(MuiButton)(({ theme }) =>
    theme.unstable_sx({
        flexShrink: 0,
        alignSelf: { xs: "flex-start", md: "center" },
        ml: { xs: 0, md: 6 },
        px: 5,
    }),
);

export const AttentionAlert = styled(Box)(({ theme }) =>
    theme.unstable_sx({
        backgroundColor: "#E9F2E7",
        borderRadius: 1,
        p: 3,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
    }),
);

export const AttentionIcon = styled(Box)({
    width: "2.4rem",
    height: "2.4rem",
    color: "#538B4C",
    flexShrink: 0,
    display: "flex",
});

// ─── Amount Input ────────────────────────────────────

export const AmountInputWrapper = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: "row",
        alignItems: "center",
        flexShrink: { md: 1, xs: 0 },
    }),
);

export const AmountIconButton = styled(MuiIconButton)({});

export const AmountField = styled(InputBase)(({ theme }) =>
    theme.unstable_sx({
        width: 105,
        height: 42,
        p: 3,
        border: "1px solid #ccc",
        borderRadius: 2,
        backgroundColor: "#fff",
        fontWeight: 600,
        fontSize: "2rem",
        "& .MuiInputBase-input": {
            p: 0,
            textAlign: "right",
        },
    }),
);

export const StepIcon = styled(Box)({
    width: "2.4rem",
    height: "2.4rem",
    display: "flex",
});

// ─── Slider ──────────────────────────────────────────

export const PensionSlider = styled(MuiSlider)({
    "& .MuiSlider-mark": {
        width: 24,
        height: 24,
        marginLeft: -13,
        backgroundImage: "url('/mark.svg')",
        backgroundPosition: "42px 45px",
        color: "transparent",
    },
    "& .MuiSlider-markLabel": {
        fontWeight: 600,
    },
    "& .MuiSlider-rail": {
        backgroundColor: "#5B9853",
    },
    "& .MuiSlider-thumb": {
        width: 40,
        height: 28,
        border: 0,
        borderRadius: "40%",
        backgroundImage: "url('/slider.svg')",
    },
});

// ─── Slider Description ──────────────────────────────

export const SliderDescriptionRoot = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        mb: { xs: 4, md: 2 },
        p: { xs: 4, md: 0 },
        backgroundColor: { xs: "#f7f7f7", md: "#fff" },
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        gap: { xs: 2, md: 0 },
    }),
);

export const SliderDescriptionGroup = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 0 },
    }),
);

export const SliderDescriptionItem = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: { xs: "row", md: "column" },
        justifyContent: { xs: "space-between", md: "flex-start" },
    }),
);

export const SliderDescMinItem = styled(SliderDescriptionItem)(({ theme }) =>
    theme.unstable_sx({
        maxWidth: { md: 130 },
    }),
);

export const SliderDescSpacer = styled(SliderDescriptionItem)(({ theme }) =>
    theme.unstable_sx({
        maxWidth: { md: 130 },
        ml: { md: 1 },
    }),
);

export const SliderDescMidItem = styled(SliderDescriptionItem)(({ theme }) =>
    theme.unstable_sx({
        maxWidth: { md: 250 },
    }),
);

export const SliderDescMidText = styled(BodyTextM)(({ theme }) =>
    theme.unstable_sx({
        mb: 0,
        ml: { md: "150px" },
    }),
);

export const SliderDescMaxItem = styled(SliderDescriptionItem)(({ theme }) =>
    theme.unstable_sx({
        maxWidth: { md: 140 },
    }),
);

export const ArrowWrapper = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        ml: "20px",
    }),
);

export const ArrowWrapperMid = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        ml: "135px",
    }),
);

export const ArrowWrapperEnd = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        alignItems: "flex-end",
        mr: "18px",
    }),
);
